import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, MenuItem, Chip, Divider, Collapse,
  InputAdornment, IconButton, Tooltip,
} from "@mui/material";
import CurrencyRupeeIcon      from "@mui/icons-material/CurrencyRupee";
import InventoryIcon          from "@mui/icons-material/Inventory2Outlined";
import TrendingDownIcon       from "@mui/icons-material/TrendingDown";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import ExpandLessIcon         from "@mui/icons-material/ExpandLess";
import FilterAltIcon          from "@mui/icons-material/FilterAlt";
import RestartAltIcon         from "@mui/icons-material/RestartAlt";
import SearchIcon             from "@mui/icons-material/Search";
import { accessoriesService } from "../service/AccessoriesService";

// ── Category color map ────────────────────────────────────────────────────────
const CAT_COLORS = {
  "Helmet":     { bg: "#e3f2fd", border: "#1565c0", text: "#1565c0", icon: "🪖" },
  "Seat Cover": { bg: "#f3e5f5", border: "#7b1fa2", text: "#7b1fa2", icon: "🪑" },
  "Bike Cover": { bg: "#e8f5e9", border: "#2e7d32", text: "#2e7d32", icon: "🛡️" },
  "Gift":       { bg: "#fff8e1", border: "#f57f17", text: "#f57f17", icon: "🎁" },
};

// 🎁 NEW — categories now arrive as "Gift: <name>" (one per gift name),
// not the bare string "Gift" — this resolves the right style for any of them.
const getCategoryStyle = (name) => {
  if (name.startsWith("Gift: ")) return CAT_COLORS["Gift"];
  return CAT_COLORS[name] || { bg: "#f5f5f5", border: "#9e9e9e", text: "#424242", icon: "📦" };
};

const ITEM_OPTIONS = ["Helmet", "Seat Cover", "Bike Cover", "Gift"];

const fmt  = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtR = (n) => `₹${fmt(n)}`;

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, color = "#1565c0", icon }) => (
  <Paper variant="outlined" sx={{
    p: 1.5, borderRadius: 2, borderColor: `${color}44`,
    bgcolor: `${color}0d`, display: "flex", alignItems: "center", gap: 1.5
  }}>
    <Box sx={{ fontSize: 22 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600}
        sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.65rem" }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={800} color={color} sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

// ── Category Block ─────────────────────────────────────────────────────────────
const CategoryBlock = ({ name, data }) => {
  const [open, setOpen] = useState(true);
  const c = getCategoryStyle(name); // 🎁 CHANGED — was: CAT_COLORS[name] || { ... }

  // ✅ NEW: individual sales block (location-wise + P&L)
  const ind = data.individualSales || { totalQty: 0, totalFifoCost: 0, totalSellingAmount: 0, totalProfitLoss: 0, status: "PROFIT", byLocation: [] };
  const indProfitColor = ind.totalProfitLoss >= 0 ? "#2e7d32" : "#d32f2f";

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", borderColor: c.border, mb: 2.5 }}>

      {/* ── Category Header ── */}
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          px: 2.5, py: 1.5, cursor: "pointer",
          bgcolor: c.bg, borderBottom: `1px solid ${c.border}44`,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontSize={22}>{c.icon}</Typography>
          <Typography variant="h6" fontWeight={800} color={c.text}>{name}</Typography>
        </Box>

        {/* Quick stats in header */}
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Chip size="small" label={`Purchased: ${fmt(data.totalPurchasedQty)} units`}
            sx={{ bgcolor: "#fff", fontWeight: 700, fontSize: "0.7rem", borderColor: c.border, border: "1px solid" }} />
          <Chip size="small" label={`Sold: ${fmt(data.totalSoldQty)} units`}
            sx={{ bgcolor: c.border, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
          {/* ✅ NEW: individual sold qty chip */}
          <Chip size="small" label={`Individual: ${fmt(ind.totalQty)} units`}
            sx={{ bgcolor: "#fff", fontWeight: 700, fontSize: "0.7rem", borderColor: "#9c27b0", border: "1px solid", color: "#9c27b0" }} />
          <Chip size="small" label={`Remaining: ${fmt(data.remainingQty)}`}
            sx={{ bgcolor: "#fff", fontWeight: 700, fontSize: "0.7rem", borderColor: data.remainingQty < 5 ? "#f44336" : c.border, border: "1px solid",
              color: data.remainingQty < 5 ? "#f44336" : c.text }} />
          <IconButton size="small">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
        </Box>
      </Box>

      <Collapse in={open}>
        <Box p={2}>

          {/* ── Summary stat cards ── */}
          <Grid container spacing={1.5} mb={2}>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Total Purchased Qty" value={fmt(data.totalPurchasedQty)} color={c.text} icon="📦" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Total Purchase Amt" value={fmtR(data.totalPurchaseAmount)} color={c.text} icon="💰" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Total Sold Qty" value={fmt(data.totalSoldQty)} color={c.text} icon="🛒" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="FIFO Sell Cost" value={fmtR(data.totalFifoSellCost)} color={c.text} icon="📊" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Remaining Stock" value={fmt(data.remainingQty)}
                color={data.remainingQty < 5 ? "#f44336" : c.text} icon="🏷️" />
            </Grid>
          </Grid>

          {/* ── Date-wise table ── */}
          {data.dateWise && data.dateWise.length > 0 ? (
            <>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1 }}>
                Date-wise Breakdown
              </Typography>
              <Box sx={{ overflowX: "auto", borderRadius: 1.5, border: `1px solid ${c.border}33`, mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: c.bg }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: c.text }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.72rem", color: c.text }}>Sold Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.72rem", color: c.text }}>Avg FIFO Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.72rem", color: c.text }}>FIFO Sell Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.dateWise.map((row, i) => (
                      <TableRow key={i} sx={{ "&:hover": { bgcolor: `${c.bg}` } }}>
                        <TableCell sx={{ fontSize: "0.8rem" }}>{row.date}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(row.soldQty)}</TableCell>
                        <TableCell align="right">{fmtR(row.avgFifoPrice)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: c.text }}>{fmtR(row.fifoSellCost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>

                  {/* Subtotal row */}
                  <TableBody>
                    <TableRow sx={{ bgcolor: `${c.border}15` }}>
                      <TableCell sx={{ fontWeight: 800, fontSize: "0.8rem", color: c.text }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: c.text }}>{fmt(data.totalSoldQty)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: c.text }}>
                        {data.totalSoldQty > 0 ? fmtR(Math.round(data.totalFifoSellCost / data.totalSoldQty)) : "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: c.text }}>{fmtR(data.totalFifoSellCost)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </>
          ) : (
            <Box sx={{ py: 3, textAlign: "center", mb: 3 }}>
              <Typography variant="body2" color="text.secondary">No sales in this period</Typography>
            </Box>
          )}

          {/* ── ✅ NEW: Individual Sales — Location-wise + P&L ── */}
          <Divider sx={{ mb: 2 }} />
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
              Individual Sales — Location-wise
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip size="small" label={`Selling: ${fmtR(ind.totalSellingAmount)}`}
                sx={{ fontSize: "0.7rem", bgcolor: "#f3e5f5", fontWeight: 700 }} />
              <Chip size="small"
                label={`${ind.status === "PROFIT" ? "Profit" : "Loss"}: ${fmtR(Math.abs(ind.totalProfitLoss))}`}
                sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#fff",
                  bgcolor: indProfitColor }} />
            </Box>
          </Box>

          {ind.byLocation && ind.byLocation.length > 0 ? (
            <Box sx={{ overflowX: "auto", borderRadius: 1.5, border: "1px solid #9c27b033" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f3e5f5" }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#7b1fa2" }}>Location</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#7b1fa2" }}>Sold Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#7b1fa2" }}>FIFO Cost</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#7b1fa2" }}>Selling Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#7b1fa2" }}>Profit / Loss</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ind.byLocation.map((row, i) => (
                    <TableRow key={i} sx={{ "&:hover": { bgcolor: "#f3e5f5" } }}>
                      <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>{row.location}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(row.soldQty)}</TableCell>
                      <TableCell align="right">{fmtR(row.fifoCost)}</TableCell>
                      <TableCell align="right">{fmtR(row.sellingAmount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: row.status === "PROFIT" ? "#2e7d32" : "#d32f2f" }}>
                        {row.status === "PROFIT" ? "+" : "-"}{fmtR(Math.abs(row.profitLoss))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {/* Subtotal row */}
                <TableBody>
                  <TableRow sx={{ bgcolor: "#9c27b015" }}>
                    <TableCell sx={{ fontWeight: 800, fontSize: "0.8rem", color: "#7b1fa2" }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#7b1fa2" }}>{fmt(ind.totalQty)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#7b1fa2" }}>{fmtR(ind.totalFifoCost)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#7b1fa2" }}>{fmtR(ind.totalSellingAmount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: indProfitColor }}>
                      {ind.status === "PROFIT" ? "+" : "-"}{fmtR(Math.abs(ind.totalProfitLoss))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">No individual sales in this period</Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
const AccessoriesExpancePage = () => {
  const [categories, setCategories] = useState({});
  const [grandTotal, setGrandTotal] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const currentYear = new Date().getFullYear();

  const [filters, setFilters] = useState({
    filterMode: "year",   // "year" | "month" | "range"
    year:       currentYear,
    month:      "",
    startDate:  "",
    endDate:    "",
    itemType:   "",
  });

  const handleChange = (e) =>
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const buildParams = (f) => {
    const p = {};
    if (f.itemType) p.itemType = f.itemType;

    if (f.filterMode === "range") {
      if (f.startDate) p.startDate = f.startDate;
      if (f.endDate)   p.endDate   = f.endDate;
    } else if (f.filterMode === "month") {
      if (f.year)  p.filterYear = f.year;
      if (f.month) p.month      = f.month;
    } else {
      if (f.year) p.year = f.year;
    }
    return p;
  };

  const fetchData = async (f = filters) => {
    try {
      setLoading(true); setError("");
      const params = buildParams(f);
      const res = await accessoriesService.getTotalAccessoriesReport(params);
      setCategories(res.categories || {});
      setGrandTotal(res.grandTotal || {});
      setAppliedFilters(res.filters || {});
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSearch = () => fetchData(filters);

  const handleReset = () => {
    const reset = {
      filterMode: "year", year: currentYear,
      month: "", startDate: "", endDate: "", itemType: "",
    };
    setFilters(reset);
    fetchData(reset);
  };

  const MONTHS = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>

      {/* ── PAGE TITLE ── */}
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <Box sx={{ p: 1, bgcolor: "#e3f2fd", borderRadius: 2, display: "flex" }}>
          <TrendingDownIcon sx={{ color: "#1565c0", fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#0d1b2a">
            Accessories Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            FIFO-based purchase cost tracking per category
          </Typography>
        </Box>
      </Box>

      {/* ── FILTER PANEL ── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, borderColor: "#e0e0e0" }}>

        {/* Filter mode toggle */}
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ alignSelf: "center", mr: 1, textTransform: "uppercase", letterSpacing: 1 }}>
            Filter by:
          </Typography>
          {[
            { val: "year",  label: "Year" },
            { val: "month", label: "Month" },
            { val: "range", label: "Date Range" },
          ].map((opt) => (
            <Chip
              key={opt.val}
              label={opt.label}
              size="small"
              onClick={() => setFilters((p) => ({ ...p, filterMode: opt.val }))}
              sx={{
                fontWeight: 700, cursor: "pointer",
                bgcolor: filters.filterMode === opt.val ? "#1565c0" : "#f5f5f5",
                color:   filters.filterMode === opt.val ? "#fff"    : "#555",
                "&:hover": { bgcolor: filters.filterMode === opt.val ? "#1565c0" : "#e0e0e0" }
              }}
            />
          ))}
        </Box>

        <Grid container spacing={2} alignItems="center">

          {/* ── YEAR mode ── */}
          {filters.filterMode === "year" && (
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select fullWidth size="small" label="Year"
                name="year" value={filters.year} onChange={handleChange}
              >
                {[currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {/* ── MONTH mode ── */}
          {filters.filterMode === "month" && (<>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select fullWidth size="small" label="Year"
                name="year" value={filters.year} onChange={handleChange}
              >
                {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <TextField
                select fullWidth size="small" label="Month"
                name="month" value={filters.month} onChange={handleChange}
              >
                <MenuItem value="">All Months</MenuItem>
                {MONTHS.map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </>)}

          {/* ── DATE RANGE mode ── */}
          {filters.filterMode === "range" && (<>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth size="small" label="Start Date"
                name="startDate" type="date" value={filters.startDate}
                onChange={handleChange} InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth size="small" label="End Date"
                name="endDate" type="date" value={filters.endDate}
                onChange={handleChange} InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </>)}

          {/* ── Item Type filter (always visible) ── */}
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select fullWidth size="small" label="Item Type"
              name="itemType" value={filters.itemType} onChange={handleChange}
            >
              <MenuItem value="">All Categories</MenuItem>
              {ITEM_OPTIONS.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* ── Action buttons ── */}
          <Grid item xs={6} sm={3} md={2}>
            <Button fullWidth variant="contained" onClick={handleSearch} disabled={loading}
              startIcon={<SearchIcon />}
              sx={{ borderRadius: 1.5, fontWeight: 700, bgcolor: "#1565c0",
                "&:hover": { bgcolor: "#0d47a1" } }}>
              Search
            </Button>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Button fullWidth variant="outlined" onClick={handleReset} disabled={loading}
              startIcon={<RestartAltIcon />}
              sx={{ borderRadius: 1.5, fontWeight: 700 }}>
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Applied filter chips */}
        {appliedFilters?.dateRange && (
          <Box display="flex" gap={1} mt={1.5} flexWrap="wrap" alignItems="center">
            <FilterAltIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">Active:</Typography>
            {appliedFilters.dateRange.from && (
              <Chip size="small" label={`From: ${appliedFilters.dateRange.from}`}
                sx={{ fontSize: "0.7rem", bgcolor: "#e3f2fd" }} />
            )}
            {appliedFilters.dateRange.to && (
              <Chip size="small" label={`To: ${appliedFilters.dateRange.to}`}
                sx={{ fontSize: "0.7rem", bgcolor: "#e3f2fd" }} />
            )}
            {appliedFilters.itemType && (
              <Chip size="small" label={`Type: ${appliedFilters.itemType}`}
                sx={{ fontSize: "0.7rem", bgcolor: "#f3e5f5" }} />
            )}
          </Box>
        )}
      </Paper>

      {/* ── LOADING ── */}
      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {/* ── ERROR ── */}
      {error && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: "#f44336", bgcolor: "#fff5f5", mb: 2, borderRadius: 2 }}>
          <Typography color="error" fontWeight={600}>⚠️ {error}</Typography>
        </Paper>
      )}

      {/* ── GRAND TOTAL SUMMARY BAR ── */}
      {!loading && Object.keys(grandTotal).length > 0 && (
        <Paper variant="outlined" sx={{
          p: 2, mb: 3, borderRadius: 2,
          background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
          border: "none"
        }}>
          <Typography variant="subtitle2" fontWeight={700} color="rgba(255,255,255,0.8)"
            sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1.5, fontSize: "0.7rem" }}>
            Grand Total (All Categories)
          </Typography>
          <Grid container spacing={2}>
            {[
              { label: "Total Purchased", value: `${fmt(grandTotal.totalPurchasedQty)} units` },
              { label: "Purchase Amount", value: fmtR(grandTotal.totalPurchaseAmount) },
              { label: "Total Sold",      value: `${fmt(grandTotal.totalSoldQty)} units` },
              { label: "FIFO Sell Cost",  value: fmtR(grandTotal.totalFifoSellCost) },
              { label: "Remaining Stock", value: `${fmt(grandTotal.remainingQty)} units` },
              // ✅ NEW: individual sales grand totals
              { label: "Individual Sold", value: `${fmt(grandTotal.individualSoldQty)} units` },
              { label: "Individual P/L",  value: `${grandTotal.individualProfitLoss >= 0 ? "+" : "-"}${fmtR(Math.abs(grandTotal.individualProfitLoss || 0))}` },
            ].map(({ label, value }) => (
              <Grid item xs={6} sm={4} md key={label}>
                <Box>
                  <Typography variant="caption" color="rgba(255,255,255,0.7)"
                    sx={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.8 }}>
                    {label}
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="#fff" sx={{ lineHeight: 1.2 }}>
                    {value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ── CATEGORY BLOCKS ── */}
      {!loading && Object.keys(categories).length > 0 &&
        Object.entries(categories).map(([name, data]) => (
          <CategoryBlock key={name} name={name} data={data} />
        ))
      }

      {/* ── EMPTY STATE ── */}
      {!loading && Object.keys(categories).length === 0 && !error && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: "center", borderRadius: 2, borderStyle: "dashed" }}>
          <InventoryIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No data found</Typography>
          <Typography variant="body2" color="text.secondary">Try adjusting your filters</Typography>
        </Paper>
      )}

    </Box>
  );
};

export default AccessoriesExpancePage;